// ============================================================================
// Task action modals (AFB-0012):
//   - ResolveModal  — close a task with a REQUIRED resolution note + status
//                     (Done / Deferred). Replaces the bare onComplete and the
//                     raw window.prompt defer. The note is written to
//                     completion_notes AND appended to the comment thread.
//   - ReassignModal — first-class hand-off with an employee picker. Relies on
//                     the widened facility_tasks_self_update policy so the
//                     current assignee can reassign to anyone.
//
// Both are presentational: they collect input and call onSubmit. The parent
// owns the DB write (optimistic updateStatus in the list/pane hooks, or the
// standalone helpers on the detail page) and fires the toast.
// ============================================================================

import { useEffect, useState, type ReactNode } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { useEmployeeOptions, employeeLabel } from './hooks/use-task-form-options';

type ResolveStatus = 'Done' | 'Deferred';

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-tcs-border bg-tcs-overlay shadow-lg">
        <div className="flex items-center justify-between border-b border-tcs-border px-4 py-3">
          <h2 className="text-sm font-semibold text-tcs-text">{title}</h2>
          <button
            type="button"
            className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ResolveModal({
  open,
  taskId,
  taskDescription,
  onClose,
  onSubmit,
}: {
  open: boolean;
  taskId: string | null;
  taskDescription?: string | null;
  onClose: () => void;
  onSubmit: (status: ResolveStatus, note: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<ResolveStatus>('Done');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStatus('Done');
      setNote('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !taskId) return null;

  const canSubmit = note.trim().length > 0 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(status, note.trim());
      onClose();
    } catch (err) {
      console.error('[tasks] resolve failed', err);
      setError(errorMessage(err, 'Could not resolve the task.', 'tasks:resolve'));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Resolve task" onClose={onClose}>
      <div className="space-y-4 px-4 py-4">
        {taskDescription && (
          <p className="text-sm text-tcs-text-secondary">{taskDescription}</p>
        )}
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            Outcome
          </span>
          <select
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as ResolveStatus)}
          >
            <option value="Done">Done</option>
            <option value="Deferred">Deferred</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            Resolution note <span className="text-st-danger-fg">*</span>
          </span>
          <textarea
            required
            rows={3}
            autoFocus
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
            placeholder="What did you find / do? e.g. Confirmed $1,900/mo rate with the client."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="mt-1 block text-[11px] text-tcs-text-muted">
            Saved to the task and posted as a comment so the creator sees it.
          </span>
        </label>
        {error && (
          <div className="rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-tcs-border pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={submit}>
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" /> Resolving…
              </>
            ) : (
              'Resolve'
            )}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// SendBackModal (AFB-0047) — return a task to its creator with a REQUIRED
// reason. The parent appends the reason to the comment thread and reassigns
// assigned_to back to created_by. Not available for system-created tasks.
export function SendBackModal({
  open,
  taskId,
  creatorLabel,
  onClose,
  onSubmit,
}: {
  open: boolean;
  taskId: string | null;
  creatorLabel: string;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open || !taskId) return null;

  const canSubmit = reason.trim().length > 0 && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
      onClose();
    } catch (err) {
      console.error('[tasks] send-back failed', err);
      setError(errorMessage(err, 'Could not send the task back.', 'tasks:send-back'));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title={`Send back to ${creatorLabel}`} onClose={onClose}>
      <div className="space-y-4 px-4 py-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            Reason <span className="text-st-danger-fg">*</span>
          </span>
          <textarea
            required
            rows={3}
            autoFocus
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
            placeholder="Why is this going back? e.g. Need the room number before I can act on this."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <span className="mt-1 block text-[11px] text-tcs-text-muted">
            Posted as a comment and reassigns the task to its creator.
          </span>
        </label>
        {error && (
          <div className="rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-tcs-border pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={submit}>
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" /> Sending…
              </>
            ) : (
              'Send back'
            )}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

export function ReassignModal({
  open,
  taskId,
  currentAssignee,
  currentEid,
  onClose,
  onSubmit,
}: {
  open: boolean;
  taskId: string | null;
  currentAssignee?: string | null;
  currentEid?: string | null;
  onClose: () => void;
  onSubmit: (assignedTo: string | null) => Promise<void>;
}) {
  const { employees, isLoading } = useEmployeeOptions();
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAssignedTo(currentAssignee ?? null);
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentAssignee]);

  if (!open || !taskId) return null;

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(assignedTo);
      onClose();
    } catch (err) {
      console.error('[tasks] reassign failed', err);
      setError(errorMessage(err, 'Could not reassign the task.', 'tasks:reassign'));
      setSubmitting(false);
    }
  }

  return (
    <ModalShell title="Reassign task" onClose={onClose}>
      <div className="space-y-4 px-4 py-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
            Assign to
          </span>
          <select
            disabled={isLoading}
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
            value={assignedTo ?? ''}
            onChange={(e) => setAssignedTo(e.target.value || null)}
          >
            <option value="">Unassigned</option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {employeeLabel(emp)}
                {currentEid === emp.employee_id ? ' (me)' : ''}
              </option>
            ))}
          </select>
        </label>
        {error && (
          <div className="rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
            {error}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 border-t border-tcs-border pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={submitting} onClick={submit}>
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" /> Saving…
              </>
            ) : (
              'Reassign'
            )}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
